import { Checkbox, Tooltip, useDraggable } from '@affine/component';
import { TagService } from '@affine/core/modules/tag';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { stopPropagation } from '@affine/core/utils';
import { i18nTime } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import type { ForwardedRef, PropsWithChildren } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { Divider, Tag } from 'antd'

import { WorkbenchLink } from '../../../modules/workbench/view/workbench-link';
import {
  anchorIndexAtom,
  rangeIdsAtom,
  selectionStateAtom,
  useAtom,
} from '../scoped-atoms';
import type { PageListItemProps } from '../types';
import { useAllDocDisplayProperties } from '../use-all-doc-display-properties';
import { ColWrapper } from '../utils';
import * as styles from './page-list-item.css';
import { PageTags } from './page-tags';

const ListTitleCell = ({
  title,
  preview,
}: Pick<PageListItemProps, 'title' | 'preview'>) => {
  const [displayProperties] = useAllDocDisplayProperties();
  return (
    <div data-testid="page-list-item-title" className={styles.titleCell}>
      <div
        data-testid="page-list-item-title-text"
        className={styles.titleCellMain}
      >
        {title}
      </div>
      {preview && displayProperties.displayProperties.bodyNotes ? (
        <div
          data-testid="page-list-item-preview-text"
          className={styles.titleCellPreview}
        >
          {preview}
        </div>
      ) : null}
    </div>
  );
};

const ListIconCell = ({ icon }: Pick<PageListItemProps, 'icon'>) => {
  return (
    <div data-testid="page-list-item-icon" className={ styles.iconCell }>
      { icon }
    </div>
  );
};

const PageSelectionCell = ({
  selectable,
  onSelectedChange,
  selected,
}: Pick<PageListItemProps, 'selectable' | 'onSelectedChange' | 'selected'>) => {
  const onSelectionChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>) => {
      return onSelectedChange?.();
    },
    [ onSelectedChange ]
  );
  if (!selectable) {
    return null;
  }
  return (
    <div className={ styles.selectionCell }>
      <Checkbox
        onClick={ stopPropagation }
        checked={ !!selected }
        onChange={ onSelectionChange }
      />
    </div>
  );
};

export const PageTagsCell = ({ pageId }: Pick<PageListItemProps, 'pageId'>) => {
  const tagList = useService(TagService).tagList;
  const tags = useLiveData(tagList.tagsByPageId$(pageId));

  return (
    <div data-testid="page-list-item-tags" className={styles.tagsCell}>
      <PageTags
        tags={tags}
        hoverExpandDirection="left"
        widthOnHover="300%"
        maxItems={5}
      />
    </div>
  );
};

const PageCreateDateCell = ({
  createDate,
}: Pick<PageListItemProps, 'createDate'>) => {
  return (
    <Tooltip content={i18nTime(createDate)}>
      <div
        data-testid="page-list-item-date"
        data-date-raw={createDate}
        className={styles.dateCell}
      >
        {i18nTime(createDate, {
          relative: true,
        })}
      </div>
    </Tooltip>
  );
};

const PageUpdatedDateCell = ({
  updatedDate,
}: Pick<PageListItemProps, 'updatedDate'>) => {
  return (
    <Tooltip content={updatedDate ? i18nTime(updatedDate) : undefined}>
      <div
        data-testid="page-list-item-date"
        data-date-raw={updatedDate}
        className={styles.dateCell}
      >
        {updatedDate
          ? i18nTime(updatedDate, {
              relative: true,
            })
          : '-'}
      </div>
    </Tooltip>
  );
};

const PageListOperationsCell = ({
  operations,
}: Pick<PageListItemProps, 'operations'>) => {
  return operations ? (
    <div onClick={stopPropagation} className={styles.operationsCell}>
      {operations}
    </div>
  ) : null;
};

export const PageListItem = (props: PageListItemProps) => {
  const [displayProperties] = useAllDocDisplayProperties();
  const pageTitleElement = useMemo(() => {
    return (
      <div className={styles.dragPageItemOverlay}>
        <div className={styles.titleIconsWrapper}>
          <PageSelectionCell
            onSelectedChange={props.onSelectedChange}
            selectable={props.selectable}
            selected={props.selected}
          />
          <ListIconCell icon={props.icon} />
        </div>
        <ListTitleCell title={props.title} preview={props.preview} />
      </div>
    );
  }, [
    props.icon,
    props.onSelectedChange,
    props.preview,
    props.selectable,
    props.selected,
    props.title,
  ]);

  const { dragRef, CustomDragPreview, dragging } = useDraggable<AffineDNDData>(
    () => ({
      canDrag: props.draggable,
      data: {
        entity: {
          type: 'doc',
          id: props.pageId,
        },
        from: {
          at: 'all-docs:list',
        },
      },
    }),
    [props.draggable, props.pageId]
  );

  return (
    <>
      <PageListItemWrapper
        onClick={props.onClick}
        to={props.to}
        pageId={props.pageId}
        draggable={props.draggable}
        isDragging={dragging}
        ref={dragRef}
        pageIds={props.pageIds || []}
      >
        <ColWrapper flex={6}>
          <ColWrapper className={styles.dndCell} flex={4}>
            <div className={ styles.titleIconsWrapper }>
              <PageSelectionCell
                onSelectedChange={ props.onSelectedChange }
                selectable={ props.selectable }
                selected={ props.selected }
              />
              {
                props.docType === 'taskDocument' &&
                <svg t="1753167438931" className="icon" viewBox="0 0 1024 1024" version="1.1"
                     xmlns="http://www.w3.org/2000/svg" p-id="7080" width="20" height="20">
                  <path
                    d="M610.803092 0l326.62158 341.473882v563.156975c0 66.043697-36.347697 119.283092-102.899092 119.283093H214.549513C148.006723 1023.91395 86.05042 970.674555 86.05042 904.630857V119.283092C86.05042 53.239395 147.998118 0 214.549513 0h396.253579z"
                    fill="#5aebdb" p-id="7081"></path>
                  <path
                    d="M937.450487 481.675832L86.05042 768.421647V119.283092C86.05042 53.248 147.998118 0 214.549513 0h396.262184l326.63879 341.482487v140.193345z"
                    fill="#5aebdb" p-id="7082"></path>
                  <path
                    d="M937.94958 341.473882l-186.35079-0.516302c-66.56 0-140.279395-58.867092-140.279395-124.91079V0L937.94958 341.473882z"
                    fill="#5aebdb" p-id="7083"></path>
                  <path
                    d="M293.388908 511.956975h437.213579c5.627697 0 10.24-4.603697 10.24-10.24v-34.299698c0-5.636303-4.612303-10.24-10.24-10.24H293.388908c-5.627697 0-10.24 4.603697-10.24 10.24v34.299698c0 5.627697 4.612303 10.24 10.24 10.24z m0 147.955092h437.213579c5.627697 0 10.24-4.603697 10.24-10.24v-32.251697c0-5.627697-4.612303-10.24-10.24-10.24H293.388908c-5.627697 0-10.24 4.612303-10.24 10.24V649.680672c0 5.636303 4.612303 10.24 10.24 10.24z m0 158.711395h293.862184c5.636303 0 10.24-4.612303 10.24-10.24v-34.299697c0-5.636303-4.603697-10.24-10.24-10.24h-293.862184c-5.627697 0-10.24 4.603697-10.24 10.24v34.299697c0 5.627697 4.612303 10.24 10.24 10.24z"
                    fill="#FFFFFF" p-id="7084"></path>
                </svg>
              }
              {
                props.docType === 'document' &&
                <svg t="1749176308598" className="icon" viewBox="0 0 1024 1024" version="1.1"
                     xmlns="http://www.w3.org/2000/svg" p-id="23135" width="20" height="20">
                  <path
                    d="M943.36 258.13c-8.35-8.34-19.63-13.03-31.43-13.03H778.47V111.38c1.19-10.94-2.09-21.9-9.19-30.32a39.73 39.73 0 0 0-28.21-14.24h-637.2c-10.96 0.65-21.19 5.79-28.21 14.24-7.1 8.42-10.47 19.38-9.11 30.32v806.69c-0.08 21.16 16.24 38.71 37.31 40.11h808.06c11.8 0 23.08-4.73 31.43-13.1 8.41-8.34 13.07-19.69 13.07-31.45v-624c0.01-11.78-4.65-23.14-13.06-31.5zM200.01 557.04h311.44v89.15H200.01v-89.15z m444.9 267.44h-444.9v-89.15h444.9v89.15z m0-445.71h-444.9V200.49h444.9v178.28z m222.52 490.28h-88.95V334.27h88.95v534.78z"
                    fill="#1443ea" p-id="23136"></path>
                </svg>
              }
              {
                props.docType === 'template' &&
                <svg t="1754641042541" className="icon" viewBox="0 0 1024 1024" version="1.1"
                     xmlns="http://www.w3.org/2000/svg" p-id="13109" width="19" height="19">
                  <path
                    d="M64 0h353.92a64 64 0 0 1 64 64v233.408a64 64 0 0 1-64 64H64a64 64 0 0 1-64-64V64a64 64 0 0 1 64-64zM64 421.632h353.92a64 64 0 0 1 64 64V960a64 64 0 0 1-64 64H64a64 64 0 0 1-64-64V485.632a64 64 0 0 1 64-64zM606.08 662.592H960a64 64 0 0 1 64 64V960a64 64 0 0 1-64 64H606.08a64 64 0 0 1-64-64v-233.408a64 64 0 0 1 64-64zM606.08 0H960a64 64 0 0 1 64 64v474.368a64 64 0 0 1-64 64H606.08a64 64 0 0 1-64-64V64a64 64 0 0 1 64-64z"
                    fill="#c691f9" p-id="13110"></path>
                </svg>
              }
              {/* <ListIconCell icon={props.icon} /> */ }
            </div>
            <ListTitleCell title={ props.title } preview={ props.preview } />
          </ColWrapper>
          <ColWrapper
            flex={ 2 }
            alignment="end"
            style={ { overflow: 'visible' } }
            hidden={ !displayProperties.displayProperties.tags }
          >
            <PageTagsCell pageId={ props.pageId } />
          </ColWrapper>
        </ColWrapper>
        <ColWrapper
          flex={ 1.2 }
          alignment="end"
          hidden={ false }
        >
          {
          props.docType === 'template' &&
            <Tag color={'purple'}>模板文档</Tag>
          }
          {
            props.docType === 'document' &&
            <Tag color={ 'blue' }>普通文档</Tag>
          }
          {
            props.docType === 'taskDocument' &&
            <Tag color={ 'cyan'}>任务文档</Tag>
          }
        </ColWrapper>
        <ColWrapper
          flex={ 1 }
          alignment="end"
          hidden={ false }
        >
          {props?.createdBy}
        </ColWrapper>
        <ColWrapper
          flex={ 1 }
          alignment="end"
          hideInSmallContainer
          hidden={ !displayProperties.displayProperties.createDate }
        >
          <PageCreateDateCell createDate={ props.createDate } />
        </ColWrapper>
        <ColWrapper
          flex={ 1 }
          alignment="end"
          hideInSmallContainer
          hidden={ !displayProperties.displayProperties.updatedDate}
        >
          <PageUpdatedDateCell updatedDate={props.updatedDate} />
        </ColWrapper>
        {props.operations ? (
          <ColWrapper
            className={styles.actionsCellWrapper}
            flex={1}
            alignment="end"
          >
            <PageListOperationsCell operations={props.operations} />
          </ColWrapper>
        ) : null}
      </PageListItemWrapper>
      <CustomDragPreview position="pointer-outside">
        {pageTitleElement}
      </CustomDragPreview>
    </>
  );
};

type PageListWrapperProps = PropsWithChildren<
  Pick<PageListItemProps, 'to' | 'pageId' | 'onClick' | 'draggable'> & {
    isDragging: boolean;
    pageIds: string[];
  }
>;

const PageListItemWrapper = forwardRef(
  (
    {
      to,
      isDragging,
      pageId,
      pageIds,
      onClick,
      children,
      draggable,
    }: PageListWrapperProps,
    ref: ForwardedRef<HTMLAnchorElement & HTMLDivElement>
  ) => {
    const [selectionState, setSelectionActive] = useAtom(selectionStateAtom);
    const [anchorIndex, setAnchorIndex] = useAtom(anchorIndexAtom);
    const [rangeIds, setRangeIds] = useAtom(rangeIdsAtom);

    const handleShiftClick = useCallback(
      (currentIndex: number) => {
        if (anchorIndex === undefined) {
          setAnchorIndex(currentIndex);
          onClick?.();
          return;
        }

        const lowerIndex = Math.min(anchorIndex, currentIndex);
        const upperIndex = Math.max(anchorIndex, currentIndex);
        const newRangeIds = pageIds.slice(lowerIndex, upperIndex + 1);

        const currentSelected = selectionState.selectedIds || [];

        // Set operations
        const setRange = new Set(rangeIds);
        const newSelected = new Set(
          currentSelected.filter(id => !setRange.has(id)).concat(newRangeIds)
        );

        selectionState.onSelectedIdsChange?.([...newSelected]);
        setRangeIds(newRangeIds);
      },
      [
        anchorIndex,
        onClick,
        pageIds,
        selectionState,
        setAnchorIndex,
        rangeIds,
        setRangeIds,
      ]
    );

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!selectionState.selectable) {
          return;
        }
        e.stopPropagation();
        const currentIndex = pageIds.indexOf(pageId);

        if (e.shiftKey) {
          e.preventDefault();
          if (!selectionState.selectionActive) {
            setSelectionActive(true);
            setAnchorIndex(currentIndex);
            onClick?.();
          } else {
            handleShiftClick(currentIndex);
          }
        } else {
          setAnchorIndex(undefined);
          setRangeIds([]);
          onClick?.();
          return;
        }
      },
      [
        handleShiftClick,
        onClick,
        pageId,
        pageIds,
        selectionState.selectable,
        selectionState.selectionActive,
        setAnchorIndex,
        setRangeIds,
        setSelectionActive,
      ]
    );

    const commonProps = useMemo(
      () => ({
        role: 'list-item',
        'data-testid': 'page-list-item',
        'data-page-id': pageId,
        'data-draggable': draggable,
        className: styles.root,
        'data-clickable': !!onClick || !!to,
        'data-dragging': isDragging,
        onClick: onClick ? handleClick : undefined,
      }),
      [pageId, draggable, onClick, to, isDragging, handleClick]
    );

    useEffect(() => {
      if (selectionState.selectionActive) {
        // listen for shift key up
        const handleKeyUp = (e: KeyboardEvent) => {
          if (e.key === 'Shift') {
            setAnchorIndex(undefined);
            setRangeIds([]);
          }
        };
        window.addEventListener('keyup', handleKeyUp);
        return () => {
          window.removeEventListener('keyup', handleKeyUp);
        };
      }
      return;
    }, [
      selectionState.selectionActive,
      setAnchorIndex,
      setRangeIds,
      setSelectionActive,
    ]);

    if (to) {
      return (
        <>
          <WorkbenchLink ref={ref} {...commonProps} to={to}>
            {children}
          </WorkbenchLink>
          <div style={{ width: 'calc(100% - 40px)' }}>
            <Divider style={{ margin: '0 20px', background: '#dddede', transform: 'translateY(2px)' }} />
          </div>
        </>
      );
    } else {
      return (
        <div ref={ref} {...commonProps}>
          {children}
        </div>
      );
    }
  }
);
PageListItemWrapper.displayName = 'PageListItemWrapper';
